<template>
    <Teleport to="body">
        <Transition name="address-modal">
            <div v-if="isOpen" class="address-modal-wrapper fixed inset-0 z-[200] flex font-inter" :style="`--primary-color: ${primaryColor}`">
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/60" @click="close"></div>

                <!-- Panel -->
                <div class="address-modal-panel relative ml-auto h-full bg-white flex flex-col w-full sm:w-[480px] shadow-2xl">
                    <!-- Header -->
                    <header class="flex items-center gap-3 px-4 py-4 border-b border-gray-100 shrink-0">
                        <button @click="onBack" class="p-1 -ml-1 hover:opacity-60 transition" aria-label="Voltar">
                            <svg viewBox="0 0 24 24" style="width:1.4rem;height:1.4rem">
                                <path v-if="step === 'search'" fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
                                <path v-else fill="currentColor" d="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z"/>
                            </svg>
                        </button>
                        <h2 class="text-sm font-bold uppercase tracking-wider flex-1">
                            {{ step === 'search' ? 'Buscar endereço' : 'Confirmar local' }}
                        </h2>
                    </header>

                    <!-- STEP: Search -->
                    <div v-show="step === 'search'" class="flex flex-col flex-1 min-h-0">
                        <!-- Search Input -->
                        <div class="px-4 pt-3 pb-2 shrink-0">
                            <div class="relative">
                                <svg class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="1.8"/>
                                    <path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                </svg>
                                <input
                                    ref="searchInputEl"
                                    v-model="query"
                                    @input="onQueryInput"
                                    type="text"
                                    placeholder="Digite o endereço, rua, número..."
                                    autocomplete="off"
                                    spellcheck="false"
                                    class="w-full bg-gray-50 border border-gray-100 rounded-xl pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-gray-400 focus:bg-white transition"
                                />
                                <button
                                    v-if="query"
                                    @click="clearQuery"
                                    class="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100"
                                    aria-label="Limpar"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                        <path d="M6 6l12 12M6 18L18 6" stroke="#6b7280" stroke-width="2" stroke-linecap="round"/>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <!-- Use current location -->
                        <button
                            @click="useCurrentLocation"
                            :disabled="loadingLocation"
                            class="mx-4 mt-1 mb-2 flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition text-left disabled:opacity-60"
                        >
                            <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0" :style="{ background: 'color-mix(in srgb, var(--primary-color, #ef4444) 12%, transparent)', color: 'var(--primary-color, #ef4444)' }">
                                <svg v-if="!loadingLocation" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                    <circle cx="12" cy="12" r="6" stroke="currentColor" stroke-width="1.8"/>
                                    <circle cx="12" cy="12" r="2" fill="currentColor"/>
                                </svg>
                                <svg v-else class="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" stroke-opacity="0.25"/>
                                    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                                </svg>
                            </div>
                            <div class="flex-1">
                                <p class="text-sm font-bold" style="color: var(--primary-color, #ef4444)">
                                    {{ loadingLocation ? 'Localizando...' : 'Usar minha localização atual' }}
                                </p>
                                <p class="text-xs text-gray-500 mt-0.5">Detectaremos seu endereço pelo GPS</p>
                            </div>
                        </button>

                        <!-- Saved addresses -->
                        <div v-if="savedAddresses?.length && !query" class="px-4 pb-1">
                            <h3 class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mt-3 mb-2">Endereços salvos</h3>
                            <button
                                v-for="(addr, idx) in savedAddresses"
                                :key="idx"
                                @click="useSavedAddress(addr)"
                                class="w-full flex items-start gap-3 py-3 border-b border-gray-50 last:border-b-0 text-left hover:bg-gray-50 -mx-1 px-1 rounded-lg transition"
                            >
                                <div class="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0 mt-0.5">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 13.43a3.12 3.12 0 1 0 0-6.24 3.12 3.12 0 0 0 0 6.24z" stroke="#737373" stroke-width="1.5"/>
                                        <path d="M3.62 8.49C5.59-.16 18.42-.15 20.38 8.5c1.15 5.07-2 9.36-4.77 12.01a5.2 5.2 0 0 1-7.22 0C5.62 17.85 2.47 13.56 3.62 8.49z" stroke="#737373" stroke-width="1.5"/>
                                    </svg>
                                </div>
                                <div class="flex-1 min-w-0">
                                    <p class="text-sm font-semibold truncate">{{ (addr as any).street }}, {{ (addr as any).number }}</p>
                                    <p class="text-xs text-gray-500 truncate">{{ (addr as any).neighborhood }} · {{ (addr as any).city }}/{{ (addr as any).state }}</p>
                                </div>
                            </button>
                        </div>

                        <!-- Suggestions / States -->
                        <div class="flex-1 overflow-y-auto px-4 pb-4">
                            <template v-if="suggestions.length">
                                <h3 class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mt-3 mb-1">Resultados</h3>
                                <button
                                    v-for="(s, idx) in suggestions"
                                    :key="idx"
                                    @click="selectSuggestion(s)"
                                    class="w-full flex items-start gap-3 py-3 border-b border-gray-50 last:border-b-0 text-left hover:bg-gray-50 -mx-1 px-1 rounded-lg transition"
                                >
                                    <div class="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0 mt-0.5">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 13.43a3.12 3.12 0 1 0 0-6.24 3.12 3.12 0 0 0 0 6.24z" stroke="#737373" stroke-width="1.5"/>
                                            <path d="M3.62 8.49C5.59-.16 18.42-.15 20.38 8.5c1.15 5.07-2 9.36-4.77 12.01a5.2 5.2 0 0 1-7.22 0C5.62 17.85 2.47 13.56 3.62 8.49z" stroke="#737373" stroke-width="1.5"/>
                                        </svg>
                                    </div>
                                    <div class="flex-1 min-w-0">
                                        <p class="text-sm font-semibold truncate">{{ s.main }}</p>
                                        <p class="text-xs text-gray-500 truncate">{{ s.secondary }}</p>
                                    </div>
                                </button>
                            </template>

                            <div v-else-if="searching" class="py-10 flex flex-col items-center justify-center text-gray-400">
                                <svg class="animate-spin mb-2" width="22" height="22" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" stroke-opacity="0.25"/>
                                    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                                </svg>
                                <p class="text-xs">Buscando endereços...</p>
                            </div>

                            <div v-else-if="query && query.length >= 3" class="py-10 text-center">
                                <p class="text-sm text-gray-500">Nenhum endereço encontrado</p>
                                <p class="text-xs text-gray-400 mt-1">Tente refinar o termo da busca</p>
                            </div>
                            <div v-else-if="!query" class="py-10 text-center">
                                <div class="w-14 h-14 mx-auto rounded-full bg-gray-50 flex items-center justify-center mb-3">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <circle cx="11" cy="11" r="7" stroke="#9ca3af" stroke-width="1.5"/>
                                        <path d="M21 21l-4.35-4.35" stroke="#9ca3af" stroke-width="1.5" stroke-linecap="round"/>
                                    </svg>
                                </div>
                                <p class="text-sm text-gray-500 font-medium">Comece a digitar seu endereço</p>
                                <p class="text-xs text-gray-400 mt-1 max-w-[260px] mx-auto">Ou use sua localização atual para preenchimento automático</p>
                            </div>
                            <div v-else class="py-6 text-center">
                                <p class="text-xs text-gray-400">Digite ao menos 3 caracteres</p>
                            </div>
                        </div>

                        <!-- Type manually footer link -->
                        <div class="px-4 pb-4 pt-2 border-t border-gray-100 shrink-0">
                            <button @click="emit('manual')" class="w-full text-sm font-semibold text-gray-600 hover:text-gray-900 transition py-2">
                                Não encontrou? <span class="underline">Digitar endereço manualmente</span>
                            </button>
                        </div>
                    </div>

                    <!-- STEP: Confirm on map -->
                    <div v-show="step === 'confirm'" class="flex flex-col flex-1 min-h-0">
                        <div class="relative shrink-0">
                            <div ref="mapEl" class="w-full h-[260px] bg-gray-100"></div>
                            <!-- Center pin overlay -->
                            <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
                                <div class="relative -mt-6">
                                    <svg width="40" height="48" viewBox="0 0 40 48" fill="none">
                                        <path d="M20 0C8.95 0 0 8.95 0 20c0 14 20 28 20 28s20-14 20-28C40 8.95 31.05 0 20 0z" fill="var(--primary-color, #ef4444)"/>
                                        <circle cx="20" cy="20" r="6.5" fill="#fff"/>
                                    </svg>
                                </div>
                            </div>
                            <!-- Recenter button -->
                            <button
                                v-if="selectedLat !== null"
                                @click="recenterMap"
                                class="absolute right-3 top-3 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 z-[5]"
                                aria-label="Recentralizar"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                    <circle cx="12" cy="12" r="6" stroke="currentColor" stroke-width="1.8"/>
                                    <circle cx="12" cy="12" r="2" fill="currentColor"/>
                                </svg>
                            </button>
                        </div>

                        <div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                            <div class="flex items-start gap-3">
                                <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0 mt-0.5" :style="{ background: 'color-mix(in srgb, var(--primary-color, #ef4444) 12%, transparent)', color: 'var(--primary-color, #ef4444)' }">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 13.43a3.12 3.12 0 1 0 0-6.24 3.12 3.12 0 0 0 0 6.24z" stroke="currentColor" stroke-width="1.5"/>
                                        <path d="M3.62 8.49C5.59-.16 18.42-.15 20.38 8.5c1.15 5.07-2 9.36-4.77 12.01a5.2 5.2 0 0 1-7.22 0C5.62 17.85 2.47 13.56 3.62 8.49z" stroke="currentColor" stroke-width="1.5"/>
                                    </svg>
                                </div>
                                <div class="flex-1 min-w-0">
                                    <p class="text-sm font-bold leading-tight">{{ headlineAddress }}</p>
                                    <p v-if="secondaryAddress" class="text-xs text-gray-500 mt-1">{{ secondaryAddress }}</p>
                                </div>
                            </div>

                            <div class="space-y-2 pt-1">
                                <h4 class="text-[11px] font-bold uppercase tracking-wider text-gray-500">Detalhes</h4>
                                <div class="grid grid-cols-3 gap-2">
                                    <input
                                        v-model="formNumber"
                                        type="text"
                                        placeholder="Nº"
                                        inputmode="numeric"
                                        class="col-span-1 bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400 focus:bg-white"
                                    />
                                    <input
                                        v-model="formComplement"
                                        type="text"
                                        placeholder="Complemento (apto, bloco)"
                                        class="col-span-2 bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400 focus:bg-white"
                                    />
                                </div>
                                <input
                                    v-model="formNeighborhood"
                                    type="text"
                                    placeholder="Bairro"
                                    class="w-full bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400 focus:bg-white"
                                />
                                <input
                                    v-model="formReference"
                                    type="text"
                                    placeholder="Ponto de referência (opcional)"
                                    class="w-full bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400 focus:bg-white"
                                />
                            </div>
                        </div>

                        <div class="px-4 py-3 border-t border-gray-100 shrink-0 cart-footer">
                            <button
                                @click="confirm"
                                :disabled="!canConfirm"
                                class="w-full py-4 rounded-xl font-bold text-base flex items-center justify-center gap-2 transition"
                                :class="canConfirm ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-400 cursor-not-allowed'"
                                :style="canConfirm ? { background: 'var(--primary-color, #ef4444)' } : {}"
                            >
                                Confirmar endereço
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { useToast } from 'vue-toastification'
import { loadLeaflet, photonSearch, photonReverse, parsePhotonAddress, ParsedAddress, PhotonSuggestion } from '@/composables/useLeafletMap'
import { restaurant } from '@/global/state/GlobalState'

const primaryColor = computed(() => (restaurant.value as any)?.restaurantDesignCustom?.primaryColor || '#ef4444')

/* global defineProps, defineEmits */
// eslint-disable-next-line no-undef
const props = defineProps({
    isOpen: { type: Boolean, required: true },
    savedAddresses: { type: Array, default: () => [] }
})

// eslint-disable-next-line no-undef
const emit = defineEmits(['close', 'select', 'manual'])

const toast = useToast()

const step = ref<'search' | 'confirm'>('search')
const query = ref('')
const searching = ref(false)
const loadingLocation = ref(false)
const suggestions = ref<PhotonSuggestion[]>([])

const searchInputEl = ref<HTMLInputElement | null>(null)
const mapEl = ref<HTMLDivElement | null>(null)

const selectedLat = ref<number | null>(null)
const selectedLng = ref<number | null>(null)
const selected = ref<ParsedAddress | null>(null)

const formNumber = ref('')
const formComplement = ref('')
const formReference = ref('')
const formNeighborhood = ref('')

let map: any = null
let mapMoveTimer: any = null
let mapIdleListener: any = null
let debounceTimer: any = null
let lastReverseAt: { lat: number; lng: number } | null = null

const headlineAddress = computed(() => {
    if (!selected.value) return ''
    if (selected.value.street) {
        return selected.value.number
            ? `${selected.value.street}, ${selected.value.number}`
            : selected.value.street
    }
    return selected.value.formatted || 'Local selecionado'
})

const secondaryAddress = computed(() => {
    if (!selected.value) return ''
    const parts: string[] = []
    if (formNeighborhood.value || selected.value.neighborhood) parts.push(formNeighborhood.value || selected.value.neighborhood)
    if (selected.value.city) parts.push(selected.value.city)
    if (selected.value.state) parts.push(selected.value.state)
    return parts.join(' · ')
})

const canConfirm = computed(() => {
    return Boolean(selected.value?.street) && formNumber.value.trim().length > 0 && (formNeighborhood.value.trim().length > 0 || (selected.value?.neighborhood || '').length > 0)
})

watch(() => props.isOpen, async (open) => {
    if (open) {
        step.value = 'search'
        query.value = ''
        suggestions.value = []
        selected.value = null
        selectedLat.value = null
        selectedLng.value = null
        formNumber.value = ''
        formComplement.value = ''
        formReference.value = ''
        formNeighborhood.value = ''
        document.body.style.overflow = 'hidden'
        try {
            await loadLeaflet()
        } catch (e) {
            console.warn('[address] Leaflet failed to load', e)
            toast.error('Não foi possível carregar o mapa')
        }
        await nextTick()
        searchInputEl.value?.focus()
    } else {
        document.body.style.overflow = ''
        destroyMap()
    }
})

onBeforeUnmount(() => {
    document.body.style.overflow = ''
    destroyMap()
    if (debounceTimer) clearTimeout(debounceTimer)
    if (mapMoveTimer) clearTimeout(mapMoveTimer)
})

function close() { emit('close') }

function onBack() {
    if (step.value === 'confirm') {
        step.value = 'search'
        destroyMap()
        nextTick(() => searchInputEl.value?.focus())
    } else {
        close()
    }
}

function clearQuery() {
    query.value = ''
    suggestions.value = []
    searching.value = false
    searchInputEl.value?.focus()
}

function onQueryInput() {
    if (debounceTimer) clearTimeout(debounceTimer)
    if (!query.value || query.value.length < 3) {
        suggestions.value = []
        searching.value = false
        return
    }
    searching.value = true
    debounceTimer = setTimeout(runSearch, 300)
}

async function runSearch() {
    try {
        suggestions.value = await photonSearch(query.value, { lang: 'default', limit: 8 })
    } catch (e) {
        console.warn('[address] photon search failed', e)
        suggestions.value = []
    } finally {
        searching.value = false
    }
}

function selectSuggestion(s: PhotonSuggestion) {
    const parsed = parsePhotonAddress(s.feature)
    selected.value = parsed
    selectedLat.value = parsed.lat
    selectedLng.value = parsed.lng
    formNumber.value = parsed.number || ''
    formNeighborhood.value = parsed.neighborhood || ''
    goToConfirmStep()
}

function useSavedAddress(addr: any) {
    const parsed: ParsedAddress = {
        street: addr.street || '',
        number: addr.number || '',
        neighborhood: addr.neighborhood || '',
        city: addr.city || '',
        state: addr.state || '',
        zipCode: addr.zipCode || '',
        country: addr.country || 'BR',
        formatted: [addr.street, addr.number, addr.neighborhood, addr.city, addr.state].filter(Boolean).join(', '),
        lat: addr.lat ?? null,
        lng: addr.lng ?? null
    }
    selected.value = parsed
    selectedLat.value = parsed.lat
    selectedLng.value = parsed.lng
    formNumber.value = parsed.number
    formComplement.value = addr.complement || ''
    formReference.value = addr.reference || ''
    formNeighborhood.value = parsed.neighborhood
    if (parsed.lat && parsed.lng) {
        goToConfirmStep()
    } else {
        emit('select', { ...parsed, number: formNumber.value, complement: formComplement.value, reference: formReference.value })
    }
}

function useCurrentLocation() {
    if (!('geolocation' in navigator)) {
        toast.warning('Geolocalização não disponível neste navegador')
        return
    }
    loadingLocation.value = true
    navigator.geolocation.getCurrentPosition(
        async (pos) => {
            const { latitude, longitude } = pos.coords
            await reverseGeocode(latitude, longitude, true)
            loadingLocation.value = false
        },
        (err) => {
            loadingLocation.value = false
            const msg = err.code === err.PERMISSION_DENIED
                ? 'Permissão de localização negada. Habilite para usar este recurso.'
                : 'Não foi possível obter sua localização'
            toast.warning(msg, { timeout: 3000 })
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    )
}

async function reverseGeocode(lat: number, lng: number, advance = false) {
    try {
        const s = await photonReverse(lat, lng)
        if (s) {
            const parsed = parsePhotonAddress(s.feature)
            // Keep user-typed number if exists; otherwise use photon's
            const previousNumber = formNumber.value
            selected.value = { ...parsed, lat, lng }
            selectedLat.value = lat
            selectedLng.value = lng
            if (!previousNumber) formNumber.value = parsed.number || ''
            if (!formNeighborhood.value) formNeighborhood.value = parsed.neighborhood || ''
            if (advance) goToConfirmStep()
        } else if (advance) {
            // Even without a reverse hit, advance with empty address
            selected.value = {
                street: '', number: '', neighborhood: '', city: '', state: '', zipCode: '',
                country: 'BR', formatted: 'Local selecionado', lat, lng
            }
            selectedLat.value = lat
            selectedLng.value = lng
            goToConfirmStep()
        }
    } catch (e) {
        if (advance) toast.warning('Não foi possível identificar o endereço neste local')
    }
}

async function goToConfirmStep() {
    step.value = 'confirm'
    await nextTick()
    initMap()
}

function initMap() {
    if (!window.L || !mapEl.value || selectedLat.value === null || selectedLng.value === null) return

    const L = window.L
    const center: [number, number] = [selectedLat.value, selectedLng.value]

    if (map) {
        map.remove()
        map = null
    }

    map = L.map(mapEl.value, {
        center,
        zoom: 17,
        zoomControl: true,
        scrollWheelZoom: true,
        attributionControl: false
    })

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap'
    }).addTo(map)

    // small attribution control at bottom-right
    L.control.attribution({ position: 'bottomright', prefix: false }).addAttribution('&copy; OpenStreetMap').addTo(map)

    if (mapIdleListener) map.off('moveend', mapIdleListener)
    mapIdleListener = () => {
        if (mapMoveTimer) clearTimeout(mapMoveTimer)
        mapMoveTimer = setTimeout(onMapIdle, 500)
    }
    map.on('moveend', mapIdleListener)
    lastReverseAt = { lat: center[0], lng: center[1] }
}

function onMapIdle() {
    if (!map) return
    const c = map.getCenter()
    const lat = c.lat
    const lng = c.lng
    if (lastReverseAt) {
        const dLat = Math.abs(lat - lastReverseAt.lat)
        const dLng = Math.abs(lng - lastReverseAt.lng)
        // ~10 meters; below this, skip
        if (dLat < 0.00009 && dLng < 0.00009) return
    }
    lastReverseAt = { lat, lng }
    reverseGeocode(lat, lng, false)
}

function recenterMap() {
    if (!map || selectedLat.value === null || selectedLng.value === null) return
    map.setView([selectedLat.value, selectedLng.value], 17)
}

function destroyMap() {
    if (map) {
        try { map.remove() } catch { /* noop */ }
        map = null
    }
    if (mapMoveTimer) {
        clearTimeout(mapMoveTimer)
        mapMoveTimer = null
    }
    mapIdleListener = null
}

function confirm() {
    if (!selected.value || !canConfirm.value) return
    emit('select', {
        ...selected.value,
        neighborhood: formNeighborhood.value.trim() || selected.value.neighborhood,
        number: formNumber.value.trim(),
        complement: formComplement.value.trim(),
        reference: formReference.value.trim()
    })
}
</script>

<style scoped lang="scss">
.cart-footer {
    padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0));
}

.address-modal-enter-active .address-modal-panel,
.address-modal-leave-active .address-modal-panel {
    transition: transform 280ms cubic-bezier(.2,.8,.2,1);
}
.address-modal-enter-from .address-modal-panel,
.address-modal-leave-to .address-modal-panel {
    transform: translateX(100%);
}
.address-modal-enter-active,
.address-modal-leave-active {
    transition: opacity 220ms ease;
}
.address-modal-enter-from,
.address-modal-leave-to {
    opacity: 0;
}

@media (max-width: 640px) {
    .address-modal-panel {
        width: 100% !important;
    }
}
</style>
